import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Badge,
  Chip,
  MenuItem as MuiMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { atom } from 'jotai';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { COLORS } from '../../../theme';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import Invoice from './Invoice';
import OrderEditModal from './OrderEditModal';
import OrderDeleteModal from './OrderDeleteModal';
import OrderDetails from './OrderDetails';

import ErrorHandler from '../../stateless/Common/ErrorHandler';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import { OrderResponse } from '../../../client-sdk';
import { orderManagementParams } from './orderManagementParams';
import { orderManagementQuery } from '../../Query/orderManagementQuery';
import { isEqual } from 'lodash';
import { useDebounce } from '../../../utils/useDounce';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOrderStatusUpdateApi } from '../../../Hooks/useOrderStatusUpdateApi';
import { useOrderManagementApi } from '../../../Hooks/useOrderManagementApi';
// import ConfirmationDialog from '../../stateless/ConfirmationDialog';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import { useTranslation } from 'react-i18next';
import { audioService } from '../../../services/audioService';
import { useConfigurationSettings } from '../../../Hooks/useConfigurationSettings';

const getStatusOptions = (t: any) => [
  { value: '', label: t('orderManagement.searchFilter.selectDefault') },
  // Use API enum values for query param, keep human-readable labels
  { value: 'orderPlaced', label: t('orderManagement.status.orderPlaced') },
  { value: 'deliveryPartnerAssigned', label: t('orderManagement.status.deliveryPartnerAssigned') },
  { value: 'deliveryPartnerAccepted', label: t('orderManagement.status.deliveryPartnerAccepted') },
  { value: 'merchantAccepted', label: t('orderManagement.status.merchantAccepted') },
  { value: 'merchantRejected', label: t('orderManagement.status.merchantRejected') },
  { value: 'orderPreparing', label: t('orderManagement.status.orderPreparing') },
  { value: 'orderDispatched', label: t('orderManagement.status.orderDispatched') },
  { value: 'orderArrived', label: t('orderManagement.status.orderArrived') },
  { value: 'orderDelivered', label: t('orderManagement.status.orderDelivered') },
  { value: 'orderCancelled', label: t('orderManagement.status.orderCancelled') },
];



export interface DeliveryManagerFormValues {
  id?: string;
  question: string;
  questionInArabic: string;
  answer: string;
  answerInArabic: string;
  customerName: string;
}

interface OrderTableProps {
  orderId: string;
  setOrderId: React.Dispatch<React.SetStateAction<string>>;
  orderStatus: string;
  setOrderStatus: React.Dispatch<React.SetStateAction<string>>;
  fromDate: string;
  setFromDate: React.Dispatch<React.SetStateAction<string>>;
  toDate: string;
  setToDate: React.Dispatch<React.SetStateAction<string>>;
  autoOpenOrderId?: string;
  initialTab?: string;
}


export const deliveryManagerAtom = atom<DeliveryManagerFormValues | null>(null);
const OrderTable: React.FC<OrderTableProps> = ({ orderId,
  setOrderId,
  orderStatus,
  setOrderStatus, fromDate, setFromDate, toDate, setToDate, autoOpenOrderId, initialTab }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  
  const [_selectedRows, setSelectedRows] = useState<string[]>([]);
  const [_modalContent] = useState<'add' | 'edit'>('edit');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedOrderForDelete, setSelectedOrderForDelete] = useState<any>(null);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // const [trackingOrder,] = useState(null);
  // const navigate = useNavigate();

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();


  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  // Change default selectedTab to 'new'
  const [selectedTab, setSelectedTab] = useState<'all' | 'new' | 'selfPickup' | 'ongoing'>(
    initialTab === 'new' ? 'new' : initialTab === 'ongoing' ? 'ongoing' : 'all'
  );
  const [acceptRejectLoading, setAcceptRejectLoading] = useState<string | null>(null);
  const [acceptRejectError, setAcceptRejectError] = useState<string | null>(null);
  const [orderMode, setOrderMode] = useState<string>('');

  // Alert popup state
  const [alertPopupOpen, setAlertPopupOpen] = useState(false);
  const [alertPopupData, setAlertPopupData] = useState<{ orderNumber: string; status: string; alertTime: number } | null>(null);
  // Sound notification for delayed status updates
  // Track when alerts SHOULD fire (not when status changed)
  const alertScheduleRef = useRef<Map<string, number>>(new Map());
  const alertFiredRef = useRef<Set<string>>(new Set()); // ← Mark which alerts have fired
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize alertFiredRef from localStorage on component mount
  useEffect(() => {
    try {
      const savedFiredAlerts = localStorage.getItem('alertFiredSet');
      if (savedFiredAlerts) {
        const firedArray = JSON.parse(savedFiredAlerts);
        alertFiredRef.current = new Set(firedArray);
        console.log('📌 Loaded previously fired alerts from localStorage:', alertFiredRef.current);
      }
    } catch (error) {
      console.error('Failed to load fired alerts from localStorage:', error);
    }
  }, []);

  // Import the client SDK accept/reject API (replace with actual import)
  // import { acceptOrderApi, rejectOrderApi } from '../../../client-sdk/api';

  const { updateOrderStatus } = useOrderStatusUpdateApi();
  const { data: configSettings } = useConfigurationSettings();
  const { params, dispatch } = orderManagementParams();
  const memoizedParams = useMemo(() => params, [params]);
  const { data: latestData, isLoading, refetch } = orderManagementQuery(memoizedParams);

  // Alert when order has been in "Merchant Accepted", "Order Preparing", or "Ready for Pickup" for specified time
  useEffect(() => {
    if (!latestData?.orders || latestData.orders.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Safe extraction with defaults
    const defaultAlertSettings = {
      merchantAccepted: 1,
      orderPreparing: 30,
      readyForPickup: 10,
    };

    const orderAlertSettings = {
      merchantAccepted: Math.max(1, Number(configSettings?.[0]?.generalSettingsData?.orderAlertSettings?.merchantAccepted) || defaultAlertSettings.merchantAccepted),
      orderPreparing: Math.max(1, Number(configSettings?.[0]?.generalSettingsData?.orderAlertSettings?.orderPreparing) || defaultAlertSettings.orderPreparing),
      readyForPickup: Math.max(1, Number(configSettings?.[0]?.generalSettingsData?.orderAlertSettings?.readyForPickup) || defaultAlertSettings.readyForPickup),
    };
    const STATUS_ALERT_TIMES: { [key: string]: number } = {
      'merchant accepted': orderAlertSettings.merchantAccepted * 60 * 1000, 
      'order preparing': orderAlertSettings.orderPreparing * 60 * 1000, 
      'ready for pickup': orderAlertSettings.readyForPickup * 60 * 1000, 
    };

    const STATUSES_TO_MONITOR = ['merchant accepted', 'order preparing', 'ready for pickup'];

    const checkInterval = () => {
      const currentTime = Date.now();

      latestData.orders.forEach((order: any) => {
        const normalizedStatus = (order.currentStatus || '').toLowerCase();

        if (!STATUSES_TO_MONITOR.includes(normalizedStatus)) return;

        const alertKey = `${order.orderId}-${normalizedStatus}`;
        const alertTime = STATUS_ALERT_TIMES[normalizedStatus] || 30 * 1000;

        const trackingHistory = order.trackingHistory || [];
        const statusEntry = trackingHistory.find(
          (entry: any) => (entry.status || '').toLowerCase() === normalizedStatus
        );

        if (!statusEntry?.timestamp) return;

        const statusChangeTime = new Date(statusEntry.timestamp).getTime();

        // 🔴 EARLY EXIT: Skip if already fired (prevents duplicate alerts)
        if (alertFiredRef.current.has(alertKey)) {
          return;
        }

        // Check if we've scheduled an alert for this order+status
        const hasScheduledAlert = alertScheduleRef.current.has(alertKey);

        if (!hasScheduledAlert) {
          // NEW order in this status - schedule the alert
          const alertFireTime = statusChangeTime + alertTime;
          alertScheduleRef.current.set(alertKey, alertFireTime);

          console.log(
            `📅 ALERT SCHEDULED: Order ${order.orderNumber} "${order.currentStatus}" will alert at ${new Date(alertFireTime).toLocaleTimeString()}`
          );
          console.log(`status "${order.currentStatus}" changed at: `,statusEntry.timestamp,new Date(statusEntry.timestamp).toLocaleTimeString())
          return; // Don't fire yet, just schedule
        }

        // Get scheduled fire time
        const scheduledFireTime = alertScheduleRef.current.get(alertKey);

        // Check if it's time to fire
        if (currentTime >= scheduledFireTime!) {
          // 🟢 FIRE ALERT: Mark as fired BEFORE showing popup
          alertFiredRef.current.add(alertKey);
          alertScheduleRef.current.delete(alertKey);

          // Persist fired alerts to localStorage
          try {
            const firedArray = Array.from(alertFiredRef.current);
            localStorage.setItem('alertFiredSet', JSON.stringify(firedArray));
          } catch (error) {
            console.error('Failed to save fired alerts to localStorage:', error);
          }

          console.log(`🔔 ALERT FIRED: Order ${order.orderNumber} in "${order.currentStatus}"`);

          // Play sound
          audioService
            .playNotificationSound()
            .catch((err: any) => console.error('Failed to play sound alert:', err));

          // Show popup notification
          setAlertPopupData({
            orderNumber: order.orderNumber,
            status: order.currentStatus,
            alertTime: Math.floor(alertTime / 1000),
          });
          setAlertPopupOpen(true);
        }
      });

      // Clean up alerts for orders that are no longer in monitored statuses
      const activeKeys = new Set<string>();
      latestData.orders.forEach((order: any) => {
        const normalizedStatus = (order.currentStatus || '').toLowerCase();
        if (STATUSES_TO_MONITOR.includes(normalizedStatus)) {
          activeKeys.add(`${order.orderId}-${normalizedStatus}`);
        }
      });

      // Remove alerts for orders no longer in monitored statuses
      alertScheduleRef.current.forEach((_, key) => {
        if (!activeKeys.has(key)) {
          alertScheduleRef.current.delete(key);
          alertFiredRef.current.delete(key);
        }
      });
    };

    intervalRef.current = setInterval(checkInterval, 1000);

return () => {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }
};
  }, [latestData,configSettings]);

  // Store previous data to prevent unnecessary re-renders
  const dataRef = useRef<OrderResponse | undefined>(undefined);

  const orderData = useMemo(() => {
    if (!isEqual(dataRef.current, latestData)) {
      dataRef.current = latestData;
    }
    return dataRef.current;
  }, [latestData]);

  // Use the correct status value from the API
  const ORDER_PLACED_STATUS = 'Order Placed';
  const ORDER_DELIVERED_STATUS = 'Order Delivered';
  const ORDER_CANCELLED_STATUS = 'Order Cancelled';

  // Count of new orders (orderPlaced)
  const newOrdersCount = useMemo(() => {
    return orderData?.orders?.filter(order => order.currentStatus === ORDER_PLACED_STATUS).length || 0;
  }, [orderData]);

  // Count of self pickup orders
  const selfPickupOrdersCount = useMemo(() => {
    return orderData?.orders?.filter(order => order.deliveryType === 'selfPickup').length || 0;
  }, [orderData]);

  // Count of ongoing orders (everything that is not new, delivered or cancelled)
  const ongoingOrdersCount = useMemo(() => {
    return (
      orderData?.orders?.filter(order => {
        const status = order.currentStatus;
        return (
          status &&
          status !== ORDER_PLACED_STATUS &&
          status !== ORDER_DELIVERED_STATUS &&
          status !== ORDER_CANCELLED_STATUS
        );
      }).length || 0
    );
  }, [orderData]);

  // Filtered rows based on selected tab
  const filteredRows = React.useMemo(() => {
    if (!orderData?.orders) return [];
    let storeOwners = orderData?.orders || [];
    if (selectedTab === 'new') {
      storeOwners = storeOwners.filter((row: any) => row.currentStatus === ORDER_PLACED_STATUS);
    } else if (selectedTab === 'selfPickup') {
      storeOwners = storeOwners.filter((row: any) => row.deliveryType === 'selfPickup');
    } else if (selectedTab === 'ongoing') {
      storeOwners = storeOwners.filter((row: any) => {
        const status = row.currentStatus;
        return (
          status &&
          status !== ORDER_PLACED_STATUS &&
          status !== ORDER_DELIVERED_STATUS &&
          status !== ORDER_CANCELLED_STATUS
        );
      });
    }
    return storeOwners.map((row: any, index: number) => {
      return {
        ...row,
        id: row.orderId,
        sno: index + 1 + (Number(params.page) - 1) * Number(params.limit),
        orderNumber: row.orderNumber || 'N/A',
        invoiceAmount: Number(row.orderAmount) || 0,
        orderDate: row.orderDate,
        customerName : row.customerInfo?.name || 'N/A',
      };
    });
  }, [params.search, orderData, params.page, params.limit, selectedTab]);


  // const handleAddSubAdmin = () => {
  //   setOpenAddDialog(true);
  // };


  const handleCloseEditModal = () => {
    setIsClosingModal(true);
    setOpenEditModal(false);
    setSelectedOrderForEdit(null);
    setDirectOrderData(null);
    setDirectOrderLoading(false);
    let changed = false;
    if (searchParams.has('orderId')) {
      searchParams.delete('orderId');
      changed = true;
    }
    if (searchParams.get('tab') === 'new') {
      searchParams.delete('tab');
      changed = true;
    }
    if (changed) setSearchParams(searchParams, { replace: true });
    
    // Reset the closing flag after a short delay
    setTimeout(() => {
      setIsClosingModal(false);
    }, 100);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedOrderForDelete(null);
  };

  const handleCloseInvoice = () => {
    setOpenInvoice(false);
  };


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);


  const { showMessage } = useSnackbar();

  // Add state for direct order fetch
  const [directOrderData, setDirectOrderData] = useState<any>(null);
  const [directOrderLoading, setDirectOrderLoading] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
console.log(directOrderData)
  // Direct API call for specific order
  const { getAllOrderManagement } = useOrderManagementApi();

  const fetchOrderDirectly = useCallback(async (orderId: string) => {
    setDirectOrderLoading(true);
    try {
      // Get user data for API call
      let userId = '';
      let userType = 'Merchant';
      try {
        const storedUser = localStorage.getItem('userData');
        const parsed = storedUser ? JSON.parse(storedUser) : null;
        userId = parsed?.userId || '';
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }

      const response = await getAllOrderManagement({
        userType,
        userId,
        orderId,
        page: 1,
        limit: 1
      });

      if (response?.orders && response.orders.length > 0) {
        setDirectOrderData(response.orders[0]);
        setSelectedOrderForEdit(response.orders[0]);
        setOpenEditModal(true);
      } else {
        showMessage && showMessage(t('orderManagement.messages.orderNotFound'), 'error');
        // Clear URL params
        let changed = false;
        if (searchParams.has('orderId')) {
          searchParams.delete('orderId');
          changed = true;
        }
        if (searchParams.get('tab') === 'new') {
          searchParams.delete('tab');
          changed = true;
        }
        if (changed) setSearchParams(searchParams, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      showMessage && showMessage(t('orderManagement.messages.failedToFetch'), 'error');
      // Clear URL params on error
      let changed = false;
      if (searchParams.has('orderId')) {
        searchParams.delete('orderId');
        changed = true;
      }
      if (searchParams.get('tab') === 'new') {
        searchParams.delete('tab');
        changed = true;
      }
      if (changed) setSearchParams(searchParams, { replace: true });
    } finally {
      setDirectOrderLoading(false);
    }
  }, [getAllOrderManagement, showMessage, searchParams, setSearchParams]);

  useEffect(() => {
    if (autoOpenOrderId && !isClosingModal && !openEditModal) {
      fetchOrderDirectly(autoOpenOrderId);
    }
  }, [autoOpenOrderId, fetchOrderDirectly, isClosingModal, openEditModal]);



  // Optional: Reset page to 1 when switching tabs
  useEffect(() => {
    dispatch({ type: 'SET_PAGE', payload: 1 });
  }, [selectedTab]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionRow, setActionRow] = useState<any>(null);
  const [openDecisionModal, setOpenDecisionModal] = useState(false);
  const [decisionOrder, setDecisionOrder] = useState<any>(null);
  const [itemStatuses, setItemStatuses] = useState<{ [key: string]: string }>({});
  const [statusDropdownAnchor, setStatusDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: t('orderManagement.columns.sno'),
      width: 70,
    },
    {
      field: 'customerName',
      headerName: t('orderManagement.columns.customerName'),
      flex: 1,
    },
    {
      field: 'orderNumber',
      headerName: t('orderManagement.columns.orderId'),
      flex: 1,
    },
    {
      field: 'orderDate',
      headerName: t('orderManagement.columns.orderDate'),
      flex: 1,
      valueFormatter: (params) => {
        const value = params;
        if (!value) return '-';

        const date = new Date(value);

        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
      },
    }


    ,
    {
      field: 'orderMode',
      headerName: t('orderManagement.columns.orderMode'),
      flex: 1,
      renderCell: (params) => {
        const mode = params.row.orderMode;
        if (!mode) return '-';

        const label =
          mode === 'preOrder'
            ? t('orderManagement.orderMode.preOrder')
            : t('orderManagement.orderMode.normal');

        const color =
          mode === 'preOrder'
            ? 'warning'
            : 'default';

        return (
          <Chip
            label={label}
            color={color as any}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      },
    },
    {
      field: 'orderAmount',
      headerName: t('orderManagement.columns.orderAmount'),
      flex: 1,
      valueFormatter: (params) => {
        return params != null ? `${params}` : '-';
      },
    },
    {
      field: 'invoiceAmount',
      headerName: t('orderManagement.columns.invoiceAmount'),
      flex: 1,
      valueFormatter: (params) => {
        return params != null ? `${params}` : '-';
      },
    },
    {
      field: 'currentStatus',
      headerName: t('orderManagement.columns.orderStatus'),
      flex: 1,
    },
    {
      field: 'paymentStatus',
      headerName: t('orderManagement.columns.paymentStatus'),
      flex: 1,
    },
    {
      field: 'invoice',
      headerName: t('orderManagement.columns.invoice'),
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('orderManagement.invoice.title')} arrow>
            <Button
              sx={{ textTransform: 'none' }}
              onClick={() => {
                // setSelectedOrderId(params.row.orderId);
                setOpenInvoice(true);
                setSelectedInvoice(params.row);
              }}
            >
              {t('orderManagement.actions.view')}
            </Button>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('orderManagement.columns.actions'),
      flex: 1,
      renderCell: (params) => (
        <>
          {/* Accept/Reject for New Orders */}
          {selectedTab === 'new' && params.row.currentStatus === ORDER_PLACED_STATUS ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleOpenDecisionModal(params.row)}
              startIcon={<MoreVertIcon />}
            >
            </Button>
          ) : (
            <>
              <Button
                sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setActionRow(params.row);
                }}
              >
                <MoreVertIcon />
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && actionRow?.orderId === params.row.orderId}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem onClick={() => {
                  navigate(`/layout/orderManagement/view/${params.row.orderId}`, {
                    state: { trackingOrder: params.row }
                  });
                  setAnchorEl(null);
                }}>{t('orderManagement.actions.view')}</MenuItem>
                <MenuItem onClick={() => {
                  setSelectedOrderForEdit(params.row);
                  setOpenEditModal(true);
                  setAnchorEl(null);
                }}>{t('orderManagement.actions.edit')}</MenuItem>
                {/* <MenuItem onClick={() => {
                  setSelectedOrderForDelete(params.row);
                  setOpenDeleteModal(true);
                  setAnchorEl(null);
                }}>Delete</MenuItem> */}
              </Menu>
            </>
          )}
        </>
      ),
    },
  ];

  const handleSearchClick = () => {
    const payload = {
      orderId: orderId.trim() === '' ? undefined : orderId.trim(),
      fromDate: fromDate === '' ? undefined : fromDate,
      toDate: toDate === '' ? undefined : toDate,
      status: orderStatus === '' ? undefined : orderStatus,
      orderMode: orderMode === '' ? undefined : (orderMode as 'normal' | 'preOrder'),
    };

    dispatch({ type: 'SET_FILTERS', payload });
  };

  
  // Validation function for Order ID
  // const validateOrderId = (value: string): string => {
  //   // Remove leading spaces
  //   let trimmed = value.replace(/^\s+/, '');
    
  //   // If the value is empty after trimming, return empty string
  //   if (!trimmed) return '';
    
  //   // Check if the value contains only spaces and special characters
  //   const hasValidCharacters = /[a-zA-Z0-9]/.test(trimmed);
  //   if (!hasValidCharacters) {
  //     return '';
  //   }
    
  //   // Allow spaces after the first character, but not at the beginning
  //   return trimmed;
  // };

  //  const handleOrderIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const validatedValue = validateOrderId(e.target.value);
  //   setOrderId(validatedValue);
  // };


  const handleReset = () => {
    setOrderId('');
    setFromDate('');
    setToDate('');
    setOrderStatus('');
    setOrderMode('');
    dispatch({ type: 'RESET' }); // or manually clear each
    refetch()
  };

  const handleOpenDecisionModal = (order: any) => {
    setDecisionOrder(order);
    
    // Initialize item statuses based on isAccepted and isRejected flags
    const initialStatuses: { [key: string]: string } = {};
    (order?.orderItems || order?.items || []).forEach((item: any) => {
      if (item.isAccepted) {
        initialStatuses[item.itemId] = 'Accepted';
      } else if (item.isRejected) {
        initialStatuses[item.itemId] = 'Rejected';
      } else {
        // Both isAccepted and isRejected are false, default to Accepted
        initialStatuses[item.itemId] = 'Accepted';
      }
    });
    setItemStatuses(initialStatuses);
    
    setOpenDecisionModal(true);
  };
  const handleCloseDecisionModal = () => {
    setOpenDecisionModal(false);
    setDecisionOrder(null);
    let changed = false;
    if (searchParams.has('orderId')) {
      searchParams.delete('orderId');
      changed = true;
    }
    if (searchParams.get('tab') === 'new') {
      searchParams.delete('tab');
      changed = true;
    }
    if (changed) setSearchParams(searchParams, { replace: true });
  };
  // const handleDecision = (type: 'accept' | 'reject') => {
  //   // This function is no longer needed since we're using individual item dropdowns
  // };

  const handleStatusDropdownOpen = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setStatusDropdownAnchor(prev => ({ ...prev, [itemId]: event.currentTarget }));
  };

  const handleStatusDropdownClose = (itemId: string) => {
    setStatusDropdownAnchor(prev => ({ ...prev, [itemId]: null }));
  };

  const handleStatusChange = (itemId: string, newStatus: string) => {
    setItemStatuses(prev => ({ ...prev, [itemId]: newStatus }));
    handleStatusDropdownClose(itemId);
  };

  const getStatusDisplay = (itemId: string) => {
    const status = itemStatuses[itemId] || 'Accepted';
    return status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Accepted':
        return '#4caf50'; // Green
      case 'Rejected':
        return '#f44336'; // Red
      default:
        return '#757575'; // Gray
    }
  };
  const handleConfirmDecision = async () => {
    if (!decisionOrder) return;
    setAcceptRejectLoading(decisionOrder.orderId);
    setAcceptRejectError(null);
    try {
      // Prepare accepted and denied item IDs based on item statuses
      const acceptedItemIds: string[] = [];
      const deniedItemIds: string[] = [];

      (decisionOrder.orderItems || decisionOrder.items || []).forEach((item: any) => {
        const itemStatus = itemStatuses[item.itemId] || 'Accepted';
        if (itemStatus === 'Accepted') {
          acceptedItemIds.push(item.itemId);
        } else if (itemStatus === 'Rejected') {
          deniedItemIds.push(item.itemId);
        }
      });

      const requestData = {
        orderId: decisionOrder.orderId,
        orderNumber: decisionOrder.orderNumber,
        acceptedItemIds,
        deniedItemIds,
        status: 'MERCHANT_STATUS',
        userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
        userType: 'merchant',
        reason: 'merchant decision'
      };

      await updateOrderStatus(requestData);
      refetch();
      handleCloseDecisionModal();
    } catch (err: any) {
      setAcceptRejectError(t('orderManagement.messages.failedToSubmit'));
    } finally {
      setAcceptRejectLoading(null);
    }
  };

  return (
    <>
      {/* Tabs for All Orders / New Orders */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v as 'all' | 'new' | 'selfPickup' | 'ongoing')}
          aria-label="Order Tabs"
          textColor='inherit'
          sx={{
            '& .MuiTab-root.Mui-selected': {
              color: COLORS.secondary.main,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: COLORS.secondary.main, // bottom line color
            },
          }}
        >
          {/* Swap the order: New Orders first, then Ongoing, then Self Pickup, then All Orders */}
          <Tab
            label={
              <Badge color="error" badgeContent={newOrdersCount} invisible={newOrdersCount === 0}>
                {t('orderManagement.tabs.newOrders')}
              </Badge>
            }
            value="new"
          />
          <Tab
            label={
              <Badge color="secondary" badgeContent={ongoingOrdersCount} invisible={ongoingOrdersCount === 0}>
                {t('orderManagement.tabs.ongoingOrders')}
              </Badge>
            }
            value="ongoing"
          />
          <Tab
            label={
              <Badge color="primary" badgeContent={selfPickupOrdersCount} invisible={selfPickupOrdersCount === 0}>
                {t('orderManagement.tabs.selfPickup')}
              </Badge>
            }
            value="selfPickup"
          />
          <Tab label={t('orderManagement.tabs.allOrders')} value="all" />
        </Tabs>
      </Box>
      {/* Search Filter UI (as per screenshot) */}

      {/* Existing Search Bar and Table */}
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {acceptRejectError && (
          <Typography color="error" sx={{ mb: 2 }}>{acceptRejectError}</Typography>
        )}
        {selectedTab === 'all' && (
        <Box
          sx={{
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            background: '#f8f8f8',
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: 500, fontSize: 18, color: '#888', mb: 2 }}
          >
            {t('orderManagement.searchFilter.title')}
          </Typography>
          <Box component="form" sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {/* Left Side */}
              {/* <Box sx={{ flex: 1, minWidth: 350 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    sx={{ width: 120, fontWeight: 600, color: '#666' }}
                  >
                    Order ID :
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Order ID"
                    value={orderId}
                    // onChange={(e) => setOrderId(e.target.value)}
                    onChange={handleOrderIdChange}
                    sx={{
                      mb: 0,
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        width: '400px',
                        height: '40px',
                      },
                      '& .MuiFormLabel-root': {
                        fontSize: 14,
                      },
                    }}
                    variant="outlined"
                  />
                </Box>
                
              </Box> */}
              {/* Right Side */}
              <Box sx={{ flex: 1, minWidth: 350 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    sx={{ width: 120, fontWeight: 600, color: '#666' }}
                  >
                    {t('orderManagement.searchFilter.orderStatus')} :
                  </Typography>
                  <TextField
                    select
                    size="small"
                    placeholder={t('orderManagement.searchFilter.selectDefault')}
                    value={orderStatus}
                    onChange={(e) => setOrderStatus(e.target.value)}
                    sx={{
                      mb: 0,
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        width: '400px',
                        height: '40px',
                      },
                      '& .MuiFormLabel-root': {
                        fontSize: 14,
                      },
                    }}
                    SelectProps={{ native: true }}
                    variant="outlined"
                  >
                    {getStatusOptions(t).map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    sx={{ width: 120, fontWeight: 600, color: '#666' }}
                  >
                    {t('orderManagement.searchFilter.orderMode')} :
                  </Typography>
                  <TextField
                    select
                    size="small"
                    placeholder={t('orderManagement.searchFilter.selectDefault')}
                    value={orderMode}
                    onChange={(e) => setOrderMode(e.target.value)}
                    sx={{
                      mb: 0,
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        width: '400px',
                        height: '40px',
                      },
                      '& .MuiFormLabel-root': {
                        fontSize: 14,
                      },
                    }}
                    variant="outlined"
                  >
                    <MuiMenuItem value="">{t('orderManagement.searchFilter.selectDefault')}</MuiMenuItem>
                    <MuiMenuItem value="normal">{t('orderManagement.orderMode.normal')}</MuiMenuItem>
                    <MuiMenuItem value="preOrder">{t('orderManagement.orderMode.preOrder')}</MuiMenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{ width: 120, fontWeight: 600, color: '#666' }}
                  >
                    {t('orderManagement.searchFilter.fromDate')} :
                  </Typography>
                  <TextField
                    size="small"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{
                      mb: 0,
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        width: '400px',
                        height: '40px',
                      },
                      '& .MuiFormLabel-root': {
                        fontSize: 14,
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Typography
                    sx={{ width: 120, fontWeight: 600, color: '#666' }}
                  >
                    {t('orderManagement.searchFilter.toDate')} :
                  </Typography>
                  <TextField
                    size="small"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{
                      mb: 0,
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        width: '400px',
                        height: '40px',
                      },
                      '& .MuiFormLabel-root': {
                        fontSize: 14,
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
            {/* Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 2 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.dark,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                onClick={handleSearchClick}
              >
                {t('orderManagement.searchFilter.search')}
              </Button>
              <Button
                onClick={handleReset}
                variant="outlined"
                sx={{ textTransform: 'none', minWidth: 90, color: COLORS.secondary.main,
                  borderColor: COLORS.secondary.main }}
              >
                {t('orderManagement.searchFilter.reset')}
              </Button>
            </Box>
          </Box>
        </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}

          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder={t('orderManagement.search.placeholder')}
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSubAdmin}
              size="small"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: COLORS.primary.main,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              Add Wallet
            </Button> */}
          </Box>
        </Box>


        <EditStoreOwnerModal
          open={openInvoice}
          onClose={handleCloseInvoice}
          title={t('orderManagement.invoice.title')}
        >
          <Invoice invoiceData={selectedInvoice} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openDecisionModal}
          onClose={handleCloseDecisionModal}
          title={t('orderManagement.orderDecision.title')}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            {/* Show order details */}
            <OrderDetails order={decisionOrder} />
            {/* Accept/Reject prompt */}
            <Typography sx={{ mb: 2, mt: 2 }}>
              {t('orderManagement.orderDecision.question')}
            </Typography>
            {/* Item selection table */}
            {(decisionOrder?.orderItems || decisionOrder?.items) && (decisionOrder?.orderItems || decisionOrder?.items).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Items:</Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr style={{ background: '#f8f8f8' }}>
                        <th style={{ textAlign: 'left', padding: 4 }}>Item</th>
                        <th style={{ textAlign: 'center', padding: 4 }}>Qty</th>
                        <th style={{ textAlign: 'center', padding: 4 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(decisionOrder?.orderItems || decisionOrder?.items || []).map((item: any) => (
                        <tr key={item.itemId}>
                          <td style={{ padding: 4 }}>
                            <div>{item.itemName}</div>
                            {/* Display choice names */}
                            {item.choices && item.choices.length > 0 && (
                              <div style={{ fontSize: '0.75rem', color: '#1976d2', fontStyle: 'italic', marginTop: '2px' }}>
                                {item.choices.map((choice: any, choiceIndex: number) => (
                                  <div key={choiceIndex}>
                                    + {choice.choiceName} {choice.choicePrice > 0 && `(JOD${choice.choicePrice})`}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Display meal information */}
                            {item.mealId && item.mealName && (
                              <div style={{ marginTop: '6px', padding: '6px', backgroundColor: '#f0f7ff', borderRadius: '4px' }}>
                                <div style={{ fontSize: '0.75rem', color: '#9c27b0', fontWeight: 600 }}>
                                  🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(JOD${Number(item.mealPrice).toFixed(2)})`}
                                </div>
                                {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                                  <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                                      <div key={groupIndex}>
                                        {group.choiceGroupName}: {group.choices?.map((c: any) => c.choiceName).join(', ')}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: 4 }}>{item.quantity}</td>
                          <td style={{ textAlign: 'center', padding: 4 }}>
                            {item.isAccepted ? (
                              <Chip 
                                label={t('orderManagement.orderDecision.accepted')} 
                                color="success" 
                                size="small" 
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            ) : item.isRejected ? (
                              <Chip 
                                label={t('orderManagement.orderDecision.rejected')} 
                                color="error" 
                                size="small" 
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            ) : (
                              <Button
                                variant="outlined"
                                onClick={(e) => handleStatusDropdownOpen(e, item.itemId)}
                                endIcon={<KeyboardArrowDownIcon />}
                                size="small"
                                sx={{
                                  minWidth: 100,
                                  height: 28,
                                  justifyContent: 'space-between',
                                  textTransform: 'none',
                                  borderColor: '#ccc',
                                  color: 'white',
                                  backgroundColor: getStatusColor(getStatusDisplay(item.itemId)),
                                  '&:hover': {
                                    borderColor: '#888',
                                    backgroundColor: getStatusColor(getStatusDisplay(item.itemId)),
                                  },
                                  '& .MuiButton-endIcon': {
                                    color: 'white',
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  {getStatusDisplay(item.itemId)}
                                </Typography>
                              </Button>
                            )}
                            {!item.isAccepted && !item.isRejected && (
                              <Menu
                                anchorEl={statusDropdownAnchor[item.itemId]}
                                open={Boolean(statusDropdownAnchor[item.itemId])}
                                onClose={() => handleStatusDropdownClose(item.itemId)}
                                PaperProps={{
                                  sx: {
                                    minWidth: 100,
                                    maxHeight: 200,
                                  },
                                }}
                              >
                                <MenuItem
                                  onClick={() => handleStatusChange(item.itemId, 'Accepted')}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 1,
                                    px: 2,
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5',
                                    },
                                  }}
                                >
                                  <Typography sx={{ color: '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                                    Accepted
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      backgroundColor: '#4caf50',
                                    }}
                                  />
                                </MenuItem>
                                <MenuItem
                                  onClick={() => handleStatusChange(item.itemId, 'Rejected')}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 1,
                                    px: 2,
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5',
                                    },
                                  }}
                                >
                                  <Typography sx={{ color: '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                                    Rejected
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      backgroundColor: '#f44336',
                                    }}
                                  />
                                </MenuItem>
                              </Menu>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={acceptRejectLoading === decisionOrder?.orderId}
                onClick={() => handleConfirmDecision()}
              >
                {t('orderManagement.actions.submit')}
              </Button>
            </Box>
          </Box>
        </EditStoreOwnerModal>

         <OrderEditModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          order={selectedOrderForEdit}
          loading={directOrderLoading}
          onSuccess={() => {
            refetch();
            handleCloseEditModal();
          }}
        />

        <OrderDeleteModal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          order={selectedOrderForDelete}
          onDelete={() => {
            refetch();
            handleCloseDeleteModal();
          }}
        />

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={selectedTab === 'new' || selectedTab === 'selfPickup' ? filteredRows.length : (orderData?.pagination?.totalItems || 0)}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('orderManagement.messages.noOrdersFound')} onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
        />

        {/* Alert Popup Dialog alertPopupOpen */}
        <Dialog
          open={alertPopupOpen}
          onClose={() => setAlertPopupOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '1.5rem',
              fontWeight: 600,
              backgroundColor: '#4c0a27',
              borderBottom: '2px solid #4c0a27',
              color: '#ffff',
            }}
          >
            ⚠️ Order Alert
          </DialogTitle>
          <DialogContent sx={{ mt:2 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
      <Box sx={{ color: '#ff9800', fontSize: '1.5rem' }}>📋</Box>
      <Box>
        <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
          Order <strong>#{alertPopupData?.orderNumber}</strong> is waiting
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          This order is still in <strong>"{alertPopupData?.status}"</strong> status. Please review and update the status to keep things moving smoothly.
        </Typography>
        <Box
          sx={{
            backgroundColor: '#fff3e0',
            p: 1.5,
            borderLeft: '4px solid #ff9800',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: '#e65100', display: 'flex', alignItems: 'center', gap: 1 }}>
            💡 <strong>Tip:</strong> Update the order status to proceed to the next stage.
          </Typography>
        </Box>
      </Box>
    </Box>
  </DialogContent>
          <DialogActions sx={{ p: 1, gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAlertPopupOpen(false)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                px: 3,
              }}
            >
              Acknowledge
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};



export default OrderTable;